/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { z } from 'zod/v4-mini';
import enLocale from 'zod/v4/locales/en.js';

// Enable parse error messages
z.config(enLocale());

/**
 * Zod shape object matching type `T`, with any optional properties remapped to required properties
 * with type `originalType | undefined`.
 */
export type MatchingZodShape<T extends object> = {
  // Use Required<T> in the mapping to ensure that all properties (including optional) are
  // included in the input zod shape.
  [K in keyof Required<T>]: Omit<T, K> extends T
    ? // If K is optional in T, make it a ZodOptional
      z.ZodMiniOptional<z.ZodMiniType<T[K], T[K]>>
    : z.ZodMiniType<T[K], T[K]>;
};

/**
 * Get a Zod object that matches the expected type, including any optional properties.
 * (Does not provide the same guarantee for nested object types.)
 *
 * Unrecognized properties will be stripped/ignored at runtime.
 * To preserve them, use `matchingZodObjectLoose` instead.
 *
 * Using this helper instead of `const ZodFoo = z.object({...}); type Foo = z.infer<typeof ZodFoo>;`
 * is recommended in cases where the original type is defined elsewhere (such as in `common-types`),
 * to ensure all the properties stay in sync.
 */
export function matchingZodObject<Expected extends object>(shape: MatchingZodShape<Expected>) {
  return z.object(shape);
}

/**
 * Like `matchingZodObject`, but pass through any extra properties that aren't defined in the shape.
 *
 * See `matchingZodObject` comment for more details.
 */
export function matchingZodObjectLoose<Expected extends object>(shape: MatchingZodShape<Expected>) {
  return z.looseObject(shape);
}
